/**
 * SCRUM-376 — Preview paginado + impresión de cartelería promocional.
 * Mismo patrón que /print/etiquetas/$jobId (SCRUM-375): re-resuelve precios
 * frescos, pagina con la misma geometría de hoja, impresión manual.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { PromoSignage, type PromoSignageProduct } from "@/components/shelf-labels/PromoSignage";
import { PROMO_TEMPLATES, isPromoTemplateKey, defaultPromoStyle, defaultPromoContent, type PromoContent, type PromoStyle, type PromoTemplateKey } from "@/lib/promo-signage/templates";
import { computeSheetGeometry, paginateItems, type SheetConfig } from "@/lib/shelf-labels/sheet-layout";
import type { ShelfLabelFilter } from "@/lib/db-service/shelf-labels";

export const Route = createFileRoute("/print/carteleria/$jobId")({
  validateSearch: (raw: Record<string, unknown>) => ({
    tid: typeof raw.tid === "string" ? raw.tid : "",
  }),
  component: PromoSignagePrintPage,
  head: () => ({
    meta: [
      { title: "Cartelería — Vista de impresión" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

interface PrintJobData {
  id: string;
  priceListId: string | null;
  filters: ShelfLabelFilter;
  templateKey: string;
  sheetConfig: SheetConfig;
  visualParams: { style?: PromoStyle; content?: PromoContent } | null;
  productCount: number;
}

function PromoSignagePrintPage() {
  const { jobId } = Route.useParams();
  const { tid } = Route.useSearch();
  const [job, setJob] = useState<PrintJobData | null>(null);
  const [products, setProducts] = useState<PromoSignageProduct[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tid) { setError("Tenant no especificado."); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [{ job: fetchedJob }, tenantRes] = await Promise.all([
          apiGetJson<{ job?: PrintJobData }>(`/api/shelf-labels/print-jobs?id=${jobId}`, tid),
          apiGetJson<{ tenant?: { logoUrl?: string | null } }>("/api/tenant", tid).catch(() => ({ tenant: undefined })),
        ]);
        if (!fetchedJob) throw new Error("not_found");
        const { products: resolved } = await apiSendJson<{ products?: PromoSignageProduct[] }>(
          "/api/shelf-labels/resolve",
          "POST",
          { filter: fetchedJob.filters, priceListId: fetchedJob.priceListId },
          tid,
        );
        if (cancelled) return;
        setJob(fetchedJob);
        setProducts(resolved ?? []);
        setLogoUrl(tenantRes.tenant?.logoUrl ?? null);
      } catch {
        if (!cancelled) setError("No se pudo cargar el trabajo de impresión.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, tid]);

  const templateKey: PromoTemplateKey = job && isPromoTemplateKey(job.templateKey) ? job.templateKey : "oferta";
  const template = PROMO_TEMPLATES[templateKey];
  const style = job?.visualParams?.style ?? defaultPromoStyle();
  const content = job?.visualParams?.content ?? defaultPromoContent(template);

  const geometry = useMemo(() => (job ? computeSheetGeometry(job.sheetConfig) : null), [job]);
  const pages = useMemo(
    () => (geometry ? paginateItems(products, geometry.itemsPerPage) : []),
    [products, geometry],
  );

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }
  if (loading || !job || !geometry) {
    return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  }

  return (
    <>
      <div className="no-print flex items-center justify-between gap-4 border-b bg-gray-50 px-4 py-3">
        <span className="text-sm font-medium text-gray-700">
          {template.name} — {products.length} cartel{products.length === 1 ? "" : "es"} en {pages.length} hoja{pages.length === 1 ? "" : "s"}
          {products.length === 0 && " (sin productos)"}
        </span>
        <Button onClick={() => window.print()} disabled={products.length === 0}>Imprimir</Button>
      </div>

      {products.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">
          Ningún producto resuelto para este trabajo — puede que hayan sido desactivados o eliminados desde que se generó.
        </p>
      ) : (
        pages.map((pageItems, pageIndex) => (
          <div
            key={pageIndex}
            className="print-sheet"
            style={{
              width: `${geometry.pageWidthMm}mm`,
              height: `${geometry.pageHeightMm}mm`,
              padding: `${job.sheetConfig.marginTopMm}mm ${job.sheetConfig.marginRightMm}mm ${job.sheetConfig.marginBottomMm}mm ${job.sheetConfig.marginLeftMm}mm`,
              display: "grid",
              gridTemplateColumns: `repeat(${job.sheetConfig.columns}, ${geometry.labelWidthMm}mm)`,
              gridTemplateRows: `repeat(${job.sheetConfig.rows}, ${geometry.labelHeightMm}mm)`,
              gap: `${job.sheetConfig.gapVerticalMm}mm ${job.sheetConfig.gapHorizontalMm}mm`,
              boxSizing: "border-box",
              margin: "0 auto",
              background: "white",
            }}
          >
            {pageItems.map((product) => (
              <PromoSignage
                key={product.productId}
                product={product}
                templateKey={templateKey}
                style={style}
                content={content}
                logoUrl={logoUrl}
              />
            ))}
          </div>
        ))
      )}

      <style>{`
        @page {
          size: ${geometry.pageWidthMm}mm ${geometry.pageHeightMm}mm;
          margin: 0;
        }
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          .print-sheet { page-break-after: always; }
          .print-sheet:last-child { page-break-after: auto; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen {
          .print-sheet { margin: 16px auto; box-shadow: 0 0 0 1px rgba(0,0,0,0.1); }
        }
      `}</style>
    </>
  );
}
